import { TableLayoutHandle } from '../components/table-layout/TableLayout'
import {
	BookingData,
	DialogHandles,
	Shift,
	TableObject,
	Zone,
	DialogHandle,
	Id,
	ProcessedTableLayout,
	RectKey,
	Screen,
	BookingStep,
	Product,
	SafeZone,
	VenueData,
	VenueId,
	Shortcut,
	BaseImg,
	CacheTables,
	Hallway,
	BookingInstructions,
	MonthAvailabilityDate,
} from '../types'
import { Booking } from './booking'
import { TimeHandler } from './time-handler'
import { Translator } from './translator'
import { RumbaService } from '../services/rumba-service'
import { MetricsService } from '../services/metrics-service'
import { loadEnvValue, load } from './helpers'
import { VoiceAssistant } from './voice-assistant'
import { CacheManager } from './cache-manager'
import { CookieManager } from './cookie-manager'

/* -----------------------------------------------------------------------------
| APP MAIN STATIC VALUES
----------------------------------------------------------------------------- */
export const FALLBACK_LANG = 'en'
export const LANG: string = document.querySelector('html')?.getAttribute('lang') ?? FALLBACK_LANG
export const VENUE_ID = Number(loadEnvValue('REACT_APP_VENUE_ID')) as VenueId

export const RUMBA_API_ROOT = (() => {
	return loadEnvValue('REACT_APP_RUMBA_API')
})() as string

export const METRICS_API_ROOT = (() => {
	return loadEnvValue('REACT_APP_METRICS_API')
})() as string

export const DEFAULT_TRANSLATIONS = Object.freeze({
	'0ge_other_s': 'Others',
	'0ge_shift_s': 'Time selection',
	'0ge_ticke_n': 'Tickets selection',
	'0ge_press__': 'Press this button to continue',
	'0ge_produ__': 'Products selection',
	'0ge_pleas__': 'Please, reorganize your seats',
	'0ge_under__': 'Understood',
	'0ge_anune__': 'An unexpected error has occurred, please contact us or try again.',
	'0ge_loadi__': 'Loading',
	'0ge_retry__': 'Retry',
	'0ge_updat__': 'Update',
	'0ge_zone___': 'zone',
	'0ge_conti__': 'Continue',
	'0ge_total__': 'Total',
	'0ge_ticke__': 'Ticket',
	'0ge_ticke_s': 'Tickets',
	'0ge_onlya__': 'Only a few places left!',
	'0ge_summa__': 'Summary',
	'0ge_date___': 'Date',
	'0ge_shift__': 'Time',
	'0ge_add____': 'Add',
	'0ge_remov__': 'Remove',
	'0ge_back___': 'Back',
	'0ge_reorg__': 'Reorganize your seats',
	'0ge_apply__': 'Apply',
	'0ge_edit___': 'Edit',
	'0ge_namea__': 'Name and surname',
	'0ge_email__': 'Email',
	'0ge_phone__': 'Phone number',
	'0ge_searc__': 'Search country',
	'0ge_count__': 'Country',
	'0ge_lastr__': 'Last Rows',
	'1da_selec__': 'Select a day',
	'1da_probl__': 'Problems with your purchase?',
	'1da_write__': 'Write us via WhatsApp',
	'1da_write_e': 'Write us via email',
	'1da_soldo__': 'Sold out',
	'2sh_chang__': 'Change date',
	'2sh_chang_s': 'Change time',
	'2sh_speci__': 'Special prices',
	'2sh_selec_s': 'What time would you like to come?',
	'2di_selec_m': 'Select a schedule to see the available seats and discounts.',
	'2di_selec_q': 'Select a schedule',
	'3zo_selec_z': 'Select seats on the floor plan',
	'3zo_tocon__': 'to continue you can optionally complete the booking',
	'3zo_tocon_i': 'to continue with the booking complete the form with your information',
	'3zo_child__': "Child ticket",
	'3zo_selec__': 'Select at least 2 seats to add a child ticket.',
	'3di_singl__': 'Single tickets on a double table are subject to a surcharge.',
	'3di_child_t': 'Children\'s tickets have a fixed price',
	'3di_indic__': 'Please specify the number of children attending within your selected tickets.',
	'3di_repla__': 'Replace with children\'s tickets',
	'3di_accep__': 'Accept surcharge',
	'3di_searc__': 'Search for individual table',
	'3di_onlyo__': 'Only %s child is allowed per adult.',
	'3di_unass__': 'Unassigned seats',
	'3di_uptoy__': 'Up to 12 years old. %s Maximum 2 children per adult.',
	'3di_thisp__': 'This promotional ticket %w600 has no choice of seating. %end Seats will be assigned from the last row or free slots. Consecutive seating is not guaranteed for multiple ticket purchases.',
	'3zo_zv____1': 'It couldn\'t be any closer. Live the experience from the front rows, feel the vibration of the stage as if you were part of the group of artists.',
	'3zo_za____1': 'In Zone A you are so close that you can hear the breath of the dancers, see the expression of the singers and the movement of the musicians\' fingers. Don\'t miss a single detail.',
	'3zo_zb____1': 'Cardamomo is an intimate tablao where every seat is close to the stage. Plus, from Zone B, you’ll enjoy the show from an elevated perspective, offering a full view of the performance.',
	'3zo_zc____1': 'You cannot select your seat. We will fill the last rows or any available spots in the venue, so we cannot guarantee adjacent seating for purchases of more than one ticket.',
	'3zo_uptoy__': 'Up to 12 years old. Accompanied by at least one adult.',
	'3zo_child_a': 'Children must be accompanied',
	'3zo_there__': 'There are no free spaces adjacent to the selected adult seat. Please select an adult seat in a row with consecutive empty spaces to accommodate children.',
	'4co_compl__': 'Complete your reservation',
	'5ci_enter__': 'Enter your details',
	'5ci_promo__': 'Promotional code',
	'5ci_addco__': 'Add comments',
	'0ge_gobac__': 'Go back',
	'5ci_oopst__': 'Oops! These seats are no longer available',
	'5ci_plsea__': 'Please choose others seats to continue your booking.',
	'5pi_enter__': 'Payment information',
	'5tc_iagre__': 'I agree to the %w600 Terms & Conditions*. %end',
	'5tc_bysub__': 'By submitting this form, you consent to us storing your name, country of origin, e-mail address, telephone number and comments added, if you have provided such data. You have the right to cancel or rectify such data at any time by sending us an email to %s',
	'5su_purch__': 'Purchase summary',
	'5su_buyti__': 'Buy tickets',
	'5su_secur__': 'Secure payment',
	'5ci_ineed__': 'I need an accessible space for reduced mobility',
	'0ar_backt__': 'Back to previous booking step.',
	'0ar_tosta__': 'To start the booking process, please, select a Date.',
	'0ar_dates__': 'Date selection',
	'0ar_month__': 'Month controllers',
	'0ar_helpc__': 'Help controls',
	'0ar_click__': 'select %s',
	'0ar_onthi__': 'on this date',
	'0ar_aspec__': 'A special event takes place',
	'0ar_fewpl__': 'Few places remain',
	'0ar_nextm__': 'Next month',
	'0ar_previ__': 'Previous month',
	'0ar_table_i': 'Tables in',
	'0ar_table__': 'Table',
	'0ar_seats__': 'Seats',
	'0ar_seat___': 'Seat',
	'0ar_summa__': 'Summary',
	'0ar_selec__': 'selected',
	'0ar_notse__': 'Not selected',
	'0ar_ticke__': 'Tickets',
	'0ar_produ__': 'Products',
	'0ar_child__': 'Children',
	'0ar_close__': 'Close dialog',
	'0ar_booki__': 'Booking navigation & resume',
	'0ar_count__': 'Country selector',
	'0ar_click_t': 'click this button to make your payment',
	'0ar_front__': 'Stage front area',
	'0ar_left___': 'Stage left area',
	'0ar_right__': 'Stage right area',
	'0ar_col____': 'Column %s',
	'0ar_row____': 'Row %s',
	'0er_field__': 'Field required',
	'0er_thefi__': 'The field value exceeds the maximun number of characters',
	'0er_namea__': 'Name and surname are invalid',
	'0er_inval_e': 'Invalid email',
	'0er_inval_n': 'Invalid number',
	'0er_yourr__': 'Your reservation %s is pending verification',
	'0er_someo__': 'Some of the selected tables are already occupied. Our customer service team will help you to manage it.',

	'0ab_selec__': 'Select your entries and menu',
	'0ab_adult__': 'Adult',
	'0ab_child__': 'Children',
	'0ab_adult_t': 'Adult ticket',
	'0ab_child_t': 'Children\'s ticket',
	'0ab_ticke__': 'Tickets',
	'0ab_menus__': 'Menus',
	'0ab_optio__': 'optional',
	'0ab_perso__': 'Person',
	'0ab_peopl__': 'People',
	'0ab_whatt__': 'What time do you want to come?',
	'0ab_cance__': 'Cancellation sup.',
	'0ab_seesi__': 'See site map'
})


/* 
-----------------------------------------------------------------------------
| HARDCODED VALUES
-----------------------------------------------------------------------------
*/


export const ROUTES = {
	DATE: 'date',
	TABLE_LAYOUT: 'table-layout',
	PERSONAL_DETAILS: 'personal-details',
	CALENDAR: 'calendar',
}


export const STEP_ROUTES_MAP: Map<BookingStep, string> = new Map([
	['date', ROUTES.DATE],
	['shift', ROUTES.TABLE_LAYOUT],
	['tickets', ROUTES.TABLE_LAYOUT],
	['products', ROUTES.TABLE_LAYOUT],
	['personal-details', ROUTES.PERSONAL_DETAILS],
])
export const KEYS_USED = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']
export const TABLE_MAP_ROTATIONS: { [key: number]: number } = { 1: 270, 2: 180, 3: 180 } //{venueId: rotation}
export const MIN_QUOTIENT_TO_APPLY_SURCHARGE = 1.5
export const MIN_AVAILABILITY_PERCENT_TO_DISPLAY_FIRE_ICON_IN_ZONES = 0.5
export const FORCED_FEW_PLACES_DAYS = 3 //today included
export const SCREEN_BREAKPOINT_3 = 500
export const SCREEN_BREAKPOINT_2 = 860
export const SCREEN_BREAKPOINT_1 = 1000
export const PRODUCTS_WICH_QTY_IS_FIXED_TO_PAX: Id[] = [22, 357, 221]
export const PRODUCTS_WICH_QTY_IS_FIXED_TO_ADULTS: Id[] = []
export const PRODUCTS_WICH_QTY_IS_FIXED_TO_CHILDREN: Id[] = []
export const SLIDER_IMAGES: BaseImg[] = [
	{
		src: 'https://cardamomo.ams3.cdn.digitaloceanspaces.com/wp-content/uploads/show-y-tapas-1.jpg',
		alt: 'Food'
	},
	{
		src: 'https://cardamomo.ams3.cdn.digitaloceanspaces.com/wp-content/uploads/show-y-tapas-2.jpg',
		alt: 'Food'
	},
	{
		src: 'https://cardamomo.ams3.cdn.digitaloceanspaces.com/wp-content/uploads/show-y-tapas-3.jpg',
		alt: 'Food'
	}
]
export const VENUE_WHATSAPP_NUMBERS = {
	1: '34691022117',
	2: '34650635125',
	3: '34691022117',
}
export const APPLY_SURCHARGE_ENABLED: boolean = false
// changed: reduce venue data cache
export const CHECK_BOOKING_TABLES_INTERVAL = 1 * 60 * 1000
// export const VENUE_DATA_CACHE = 3 * 24 * 60 * 60 * 1000
export const VENUE_DATA_CACHE = 5 * 60 * 1000
export const BOOKING_STEPS_IN_ORDER: BookingStep[] = [
	'date',
	'shift',
	'tickets',
	'products',
	'personal-details',
	'payment',
]
export const SURCHARGE_NAME = 'Suplemento mesa'
export const TABLE_LAYOUT_Y_K = 1
export const TABLE_LAYOUT_X_K = 1.2
export const MIN_DISCOUNT_TO_DISPLAY_IN_CALENDAR = 0.2
export const FALLBACK_IMAGE: string = 'image-not-found.jpg'
export const CHILDREN_PER_ADULT = 50
//changed: remove unsassigned seats modal. keep last rows zone name
export const UNASIGNED_ZONES: SafeZone[] = []
export const CHILDREN_ZONES: SafeZone[] = []
export const ZONES: Zone[] = ['zv', 'za', 'zb', 'zc']
export const RECT_KEYS: RectKey[] = ['cv', 'za', 'zb', 'zc', 'zv']
export const ZONE_NAMES = { zv: 'VIP', za: 'A', za2: 'A2', zb: 'B', zc: 'c' }
export const ZONE_ORDERS = {zv: 1, za: 2, za2: 3, zb: 4, zc: 5}
export const MONTH_NAMES_LONG: string[] = Array.from({ length: 12 }, (_, month) =>
	new Date(2024, month, 3).toLocaleDateString(LANG, { month: 'long' })
)
export const MONTH_NAMES_SHORT: string[] = Array.from({ length: 12 }, (_, month) =>
	new Date(2024, month, 3).toLocaleDateString(LANG, { month: 'short' })
)
export const DAY_NAMES_LONG: string[] = Array.from({ length: 7 }, (_, offset) =>
	new Date(2024, 0, 7 + offset).toLocaleDateString(LANG, { weekday: 'long' })
)
export const DAY_NAMES_SHORT: string[] = Array.from({ length: 7 }, (_, offset) =>
	new Date(2024, 0, 7 + offset).toLocaleDateString(LANG, { weekday: 'short' })
)

/*  
-----------------------------------------------------------------------------
| DEFAULT VALUES
-----------------------------------------------------------------------------
- default values as [] may seem unnecesary, but if the data structure is
  modified in the future this will throw an error and all the values across 
  the app initializated with this default value will be ok with only changing it here.
*/
export const DEFAULT_BOOKING_DATA: BookingData = Object.freeze({
	step: 'date',
	journey: '-',
	locator: '',
	icons: [],
	venueId: VENUE_ID,
	price: 0,
	date: '', // yyyy-mm-dd hh:mm:ss
	shiftId: 0,
	eventId: 0,
	children: 0,
	pax: 0,
	meals: 0,
	tables: [],
	products: [],
	name: '',
	email: '',
	phone: {
		prefix: '',
		number: '',
	},
	country: '',
	notes: '',
	access: 0,
	acceptedTerms: false,
	discountCode: '',
})
export const DEFAULT_ROUTE: string = ROUTES.DATE
export const DEFAULT_PRODUCTS: Product[] = []
export const DEFAULT_BOOKING: Booking = {} as Booking
export const DEFAULT_FORCED_RENDERS: number = 0
export const DEFAULT_BOOKING_INSTRUCTIONS: BookingInstructions = {shiftShown: false, ticketsShown: false}
export const DEFAULT_SCREEN: Screen = { width: window.innerWidth, height: window.innerHeight }
export const DEFAULT_SHIFTS: Shift[] = []
export const DEFAULT_SHORTCUTS: Map<string, Shortcut> = new Map()
export const DEFAUL_TABLE_LAYOUT: TableObject[] = []
export const DEFAULT_MONTH_AVAILABILITY_DATE: MonthAvailabilityDate = {available: false, specialEvent: null, specialOffer: false, lastSeats: false}
export const DEFAULT_CACHE_DATE: number = new Date('2000-01-01').getTime()
export const DEFAULT_EVENT_AVAILABILITY = {zv: undefined, za: undefined, za2: undefined, zb: undefined, zc: undefined}

export const DEFAULT_TABLE_MAP_HANDLE: TableLayoutHandle = {
	focusRect: () => {},
	goToStartPosition: () => {},
	zoomOut: () => {},
	zoomIn: () => {},
	searchForIndividualTables: () => {},
	getCurrentlyFocusing: () => undefined,
	removeFocusStyles: () => {},
	searchForAccessibleTables: () => {},
	searchForDoubleTables: () => {},
	selectAdjacentChildrenSeat: () => {},
}
export const DEFAULT_VENUE_DATA: VenueData = null
export const DEFAULT_DIALOG_HANDLES: DialogHandles = new Map()
export const DEFAULT_DIALOG_HANDLE: DialogHandle = {
	openDialog: () => {},
	closeDialog: () => {},
}
export const DEFAULT_PROCESSED_TABLE_LAYOUT: ProcessedTableLayout = {
	appliedRotation: 0,
	rects: {
		cv: { lb: { x: 0, y: 0 }, lt: { x: 0, y: 0 }, rb: { x: 0, y: 0 }, rt: { x: 0, y: 0 }, width: 0, height: 0 },
		zv: { lb: { x: 0, y: 0 }, lt: { x: 0, y: 0 }, rb: { x: 0, y: 0 }, rt: { x: 0, y: 0 }, width: 0, height: 0 },
		za: { lb: { x: 0, y: 0 }, lt: { x: 0, y: 0 }, rb: { x: 0, y: 0 }, rt: { x: 0, y: 0 }, width: 0, height: 0 },
		za2: { lb: { x: 0, y: 0 }, lt: { x: 0, y: 0 }, rb: { x: 0, y: 0 }, rt: { x: 0, y: 0 }, width: 0, height: 0 },
		zb: { lb: { x: 0, y: 0 }, lt: { x: 0, y: 0 }, rb: { x: 0, y: 0 }, rt: { x: 0, y: 0 }, width: 0, height: 0 },
		zc: { lb: { x: 0, y: 0 }, lt: { x: 0, y: 0 }, rb: { x: 0, y: 0 }, rt: { x: 0, y: 0 }, width: 0, height: 0 },
	},
	processedTables: [],
}

/* 
-----------------------------------------------------------------------------
| UTILITY CLASSES
-----------------------------------------------------------------------------
*/
export const cache = new CacheManager<CacheTables>(['products', 'shifts','availability'])
export const translator = new Translator(DEFAULT_TRANSLATIONS, LANG, FALLBACK_LANG)
export const timeOuts = new TimeHandler('time-outs')
export const intervals = new TimeHandler('time-intervals')
export const booking = new Booking(load('sessionStorage','booking-data', DEFAULT_BOOKING_DATA), true)
export const cookies = new CookieManager()
export const rumba = new RumbaService(RUMBA_API_ROOT, VENUE_ID, booking.journey)
export const metrics = new MetricsService(METRICS_API_ROOT, VENUE_ID)
export const voiceAssistant = new VoiceAssistant()

//changed, used to add hallways to venue 2
export const TABLES_OVERRIDES = {
	2: {
		//bl
		86: {x: 40, y: 0},
		87: {x: 40, y: 0},
		88: {x: 40, y: 0},
		92: {x: 40, y: 0},
		93: {x: 40, y: 0},
		94: {x: 40, y: 0},
		97: {x: 40, y: 0},
		98: {x: 40, y: 0},
		99: {x: 40, y: 0},
		102: {x: 40, y: 0},
		103: {x: 40, y: 0},
		104: {x: 40, y: 0},
		//br
		89: {x: -40, y: 0},
		90: {x: -40, y: 0},
		91: {x: -110, y: 0},
		95: {x: -40, y: 0},
		96: {x: -40, y: 0},
		100: {x: -40, y: 0},
		101: {x: -40, y: 0},
		105: {x: -40, y: 0},
		106: {x: -40, y: 0},
		107: {x: -40, y: 0},
		//rt
		108: {x: 0, y: 45},
		113: {x: 0, y: 45},
		117: {x: 0, y: 45},
		121: {x: 0, y: 45},
		125: {x: 0, y: 45},
		129: {x: 0, y: 45},
		109: {x: 0, y: 45},
		114: {x: 0, y: 45},
		118: {x: 0, y: 45},
		122: {x: 0, y: 45},
		126: {x: 0, y: 45},
		130: {x: 0, y: 45},
		//rb
		110: {x: 0, y: -10},
		115: {x: 0, y: -10},
		119: {x: 0, y: -10},
		123: {x: 0, y: -10},
		127: {x: 0, y: -10},
		131: {x: 0, y: -10},
		111: {x: 0, y: -10},
		116: {x: 0, y: -10},
		120: {x: 0, y: -10},
		124: {x: 0, y: -10},
		128: {x: 0, y: -10},
		132: {x: 0, y: -10},
		112: {x: 0, y: -10},
	}
}
export const HALLHAYS: {[key: number]: Hallway[]} = {
	2: [
		{
			steps: Math.round(380 / 25),
			x: 106, 
			y: 642, 
			height: 60,
			width: 380,
			orientation: 'h' ,
			direction: 'd',
		},
		{
			steps: Math.round(250 / 25),
			x: 919, 
			y: 40, 
			height: 247,
			width: 60,
			orientation: 'v',
			direction: 'd',
		},
		{
			steps: Math.round(250 / 25),
			x: 1406, 
			y: 900, 
			height: 60,
			width: 250,
			orientation: 'h',
			direction: 'l',
		},
	]
}
